import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import DeleteBot from '../modals/DeleteBot';
import SaveChanges from '../modals/SaveChanges';
import { ArrowDownRight, Pencil, RotateCw } from 'lucide-react';
import { Bot } from 'features/bot-builder/types/apiResponses';
import { ServiceChecks } from './Services';
import { servicesApiService } from 'features/bot-builder/services/servicesApi';
import { useMutation, useQuery } from '@tanstack/react-query';
import { lifeCycleInputs } from 'features/bot-builder/types/apiRequest';
import toast from 'react-hot-toast';
import lifeCycleApiSerivces from 'features/bot-builder/services/lifeCycleApi';
import EditAdvancedConfiguration from '../modals/EditAdvanceConfig';
import Chatwithkb from '../modals/Chatwithkb';
import ServiceLimit from '../modals/ServiceLimit';

const BotCard = ({ bot }: { bot: Bot }) => {
  const [isConfig, setIsConfig] = useState(false);
  // to fetch bot data
  const { data: services, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: servicesApiService.getAllServices,
  });
  const [chatwkbOpen, setchatwkbOpen] = useState(false);

  // state to hold current state of bot information
  const [botChanges, setBotChanges] = useState<Bot>(bot);
  const [isServiceLimitOpen, setIsServiceLimitOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const getChangedFields = (original: Bot, updated: Bot): Partial<Bot> => {
    console.log(original, updated);

    return Object.keys(updated).reduce((acc, key) => {
      const originalValue = original[key as keyof Bot];
      const updatedValue = updated[key as keyof Bot];

      if (updatedValue !== originalValue) {
        // Ensure null is not assigned to fields that don't support it
        if (updatedValue !== null) {
          (acc as any)[key as keyof Bot] = updatedValue;
        }
      }
      return acc;
    }, {} as Partial<Bot>);
  };

  const handleServiceChange = (id: number, name: string) => {
    setBotChanges((prev) => {
      const isCurrentlySelected = prev.services.includes(id); // Check if already selected

      // Only open dialog when the service is being checked (added)
      if (!isCurrentlySelected && id !== 10) {
        setSelectedService({ id, name });
        setIsServiceLimitOpen(true);
      }
      const updatedServices = prev.services.includes(id)
        ? prev.services.filter((serviceId) => serviceId !== id) // Remove id if it's already in services
        : [...prev.services, id]; // Add id if it's not in services

      return {
        ...prev,
        services: updatedServices,
      };
    });
  };

  // restart bot services
  const botServiceRestart = useMutation({
    mutationKey: ['BotRestart'],
    mutationFn: lifeCycleApiSerivces.restartBot,
    onSuccess: () => {
      toast.success('bot restarted');
    },
    onError: () => {
      toast.error('Bot creation failed. Please try again!');
    },
  });

  const restartBot = (botId: string) => {
    const serviceInput: lifeCycleInputs = {
      role: 'user',
      bot_id: botId,
    };

    botServiceRestart.mutate(serviceInput);
  };

  // stop bot
  const botServiceStop = useMutation({
    mutationKey: ['BotStopped'],
    mutationFn: lifeCycleApiSerivces.stopBot,
    onSuccess: () => {
      toast.success('bot stopped');
      setBotChanges((prev) => ({ ...prev, tele_bot_status: 'STOP' }));
    },
    onError: () => {
      toast.error('Bot creation failed. Please try again!');
    },
  });

  const stopBot = (botId: string) => {
    const serviceInput: lifeCycleInputs = {
      role: 'user',
      bot_id: botId,
    };

    botServiceStop.mutate(serviceInput);
  };

  const botServiceStart = useMutation({
    mutationKey: ['BotStarted'],
    mutationFn: lifeCycleApiSerivces.startBot,
    onSuccess: () => {
      toast.success('bot started');
      setBotChanges((prev) => ({ ...prev, tele_bot_status: 'RUNNING' }));
    },
    onError: () => {
      toast.error('Bot creation failed. Please try again!');
    },
  });

  const startBot = (botId: string) => {
    const serviceInput: lifeCycleInputs = {
      role: 'user',
      bot_id: botId,
    };

    botServiceStart.mutate(serviceInput);
  };

  return (
    <div
      className={`bg-white dark:bg-[#2B2B2B] border text-text-primary ${isConfig ? 'border-primary' : 'border-gray-200 dark:border-gray-700'} p-5 rounded-md mt-6 shadow-sm dark:text-white`}
    >
      {/* heading */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0">
        {/* photo  */}
        <div className="flex flex-row gap-2 w-full sm:w-auto">
          <img
            src="/Bothistory.png"
            alt="img"
            className="rounded-md h-14 w-[54px] sm:h-14 sm:w-[54px] object-cover"
          />
          <div className="flex flex-col gap-1 w-full">
            <div className="text-[20px] sm:text-[24px] font-medium flex items-center gap-3 flex-wrap">
              {isConfig ? (
                <input
                  title="name"
                  className="text-[20px] sm:text-[24px] w-full sm:max-w-[150px] font-medium bg-background-main border-none p-1 focus:border-none focus:ring-0"
                  onChange={(e) =>
                    setBotChanges((prev) => ({ ...prev, name: e.target.value }))
                  }
                  defaultValue={bot.name}
                />
              ) : (
                <p className="text-[20px] sm:text-[24px] max-w-fit font-medium p-0 break-words">
                  {bot.name}
                </p>
              )}
              {isConfig && (
                <span>
                  <Pencil size={15} className="text-primary" />
                </span>
              )}
              <div
                className={`${isConfig ? 'hidden' : 'inline-block'} bg-secondary flex items-center justify-center rounded-md w-[51px] h-[22px]`}
              >
                <span className="text-[12px] text-primary">
                  {botChanges?.tele_bot_status === 'RUNNING'
                    ? 'Online'
                    : 'Offline'}
                </span>
              </div>
            </div>
            <div className="text-[11px] sm:text-[12px] flex flex-wrap gap-2 sm:gap-3 text-gray-500">
              <span className="uppercase">BOT ID: {bot?.id.slice(0, 12)}YF</span>
              <span className="uppercase">SERVICE ID: 7</span>
              <span className="uppercase">USERS: 250</span>
            </div>
          </div>
        </div>
        {/* change */}
        {isConfig ? (
          <div className="flex gap-2 items-center">
            <div className="flex">
              <button
                className={`${botChanges.tele_bot_status === 'RUNNING' ? 'bg-primary' : 'bg-secondary'} flex items-center justify-center rounded-l-md w-[51px] h-[22px] hover:bg-primary`}
                onClick={() => startBot(bot?.id)}
              >
                <span
                  className={`text-[12px] ${botChanges.tele_bot_status === 'RUNNING' ? 'text-white' : ' text-primary'} hover:text-white`}
                >
                  Online
                </span>
              </button>
              <button
                className={`${botChanges.tele_bot_status === 'STOP' ? 'bg-primary' : 'bg-secondary'} flex items-center justify-center rounded-r-md w-[51px] h-[22px]`}
                onClick={() => stopBot(bot?.id)}
              >
                <span
                  className={`${botChanges.tele_bot_status === 'STOP' ? 'text-white' : 'text-primary'} text-[12px] `}
                >
                  Offline
                </span>
              </button>
            </div>
            {botServiceRestart.isPending && (
              <button
                title="restart"
                type="button"
                className="animate-spin"
                disabled
              >
                <RotateCw size={15} className="text-primary" />
              </button>
            )}
            <button
              title="restart"
              type="button"
              className={`cursor-pointer ${botServiceRestart.isPending && 'hidden'}`}
              onClick={() => restartBot(bot?.id)}
            >
              <RotateCw size={15} className="text-primary" />
            </button>
          </div>
        ) : (
          <button
            title="lifecycle"
            type="button"
            className="-rotate-90 flex items-center justify-center rounded-lg w-12 h-12 bg-background-main"
          >
            <ArrowDownRight color="#846CEC" />
          </button>
        )}
      </div>

      <div className="w-full h-[1px] bg-gray-200 my-4 sm:my-6" />
      {/* messages and desc */}
      <div className="flex flex-col gap-4 sm:gap-6">
        <div className="flex flex-col gap-1">
          <h3 className="font-bold text-[14px] flex gap-2 items-center">
            Welcome message
            {isConfig && (
              <span>
                <Pencil size={15} className="text-primary" />
              </span>
            )}
          </h3>
          {isConfig ? (
            <input
              title="message"
              className="text-[14px] bg-background-main border-none p-2 font-normal focus:border-none focus:ring-0"
              onChange={(e) =>
                setBotChanges((prev) => ({
                  ...prev,
                  tele_welcome_msg: e.target.value,
                }))
              }
              defaultValue={bot.tele_welcome_msg}
              value={botChanges.tele_welcome_msg}
            />
          ) : (
            <p className="text-[14px] text-gray-600">
              Our AI handles the heavy lifting, making video accessible for everyone, even without prior editing experience. Our AI handles the heavy lifting, making
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="font-bold text-[14px] flex gap-2 items-center">
            Bot description
            {isConfig && (
              <span>
                <Pencil size={15} className="text-primary" />
              </span>
            )}
          </h3>

          {isConfig ? (
            <input
              title="description"
              className="text-[14px] bg-background-main p-2 border-none font-normal focus:border-none focus:ring-0"
              defaultValue={bot.description}
              onChange={(e) =>
                setBotChanges((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              value={botChanges.description}
            />
          ) : (
            <p className="text-[14px] text-gray-600">
              Our AI handles the heavy lifting, making video accessible for everyone, even without prior editing experience.
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-col">
          <div className="flex flex-row justify-between items-center">
            <h1 className="text-[14px] font-bold">Services</h1>
            <button className="text-xs text-blue-600 hover:underline">Advanced configuration</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-rows-5 sm:grid-cols-2 gap-3 sm:grid-flow-col sm:gap-4">
            {isLoading
              ? Array(10)
                  .fill(1)
                  .map((_, idx) => {
                    return (
                      <div
                        key={idx}
                        className="bg-gray-300 h-12 animate-pulse rounded-md"
                      />
                    );
                  })
              : services &&
                services.data.slice(0, 10).map((service, idx) => {
                  return (
                    <ServiceChecks
                      label={service.name}
                      key={`${service.name}-${idx}`}
                      disabled={!isConfig}
                      isChecked={botChanges.services.includes(service.id)}
                      onChange={() =>
                        handleServiceChange(service.id, service.name)
                      }
                      setChatWkbOpen={setchatwkbOpen}
                    />
                  );
                })}
          </div>
        </div>
      </div>

      <div className="w-full h-[1px] bg-gray-200 my-4 sm:my-6" />
      {/* buttons */}
      <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {isConfig && (
          <SaveChanges
            isDisabled={botChanges === bot}
            botChanges={getChangedFields(bot, botChanges)}
            id={bot.id}
            setIsConfig={setIsConfig}
          />
        )}
        <Button
          type="button"
          variant="customSecondary"
          title="config"
          className={`text-sm bg-secondary text-primary-600 rounded-md hover:bg-secondary/90 ${isConfig && 'hidden'}`}
          onClick={() => setIsConfig(true)}
        >
          Change configuration
        </Button>

        {isConfig ? (
          <Button
            className="text-[14px] font-medium bg-secondary text-primary-600 rounded-md hover:bg-secondary/90"
            variant="customSecondary"
            onClick={() => setIsConfig(false)}
          >
            Cancel
          </Button>
        ) : (
          <Button 
            variant="outline" 
            className="text-red-500 border-red-500 hover:bg-red-50 rounded-md"
          >
            Delete bot
          </Button>
        )}
      </div>
      {chatwkbOpen && (
        <Chatwithkb
          chatwkbOpen={chatwkbOpen}
          setchatwkbOpen={setchatwkbOpen}
          botId={bot.id}
          services={botChanges.services}
        />
      )}

      {isServiceLimitOpen && selectedService != null && (
        <ServiceLimit
          open={isServiceLimitOpen}
          setOpen={setIsServiceLimitOpen}
          serviceId={selectedService.id}
          serviceName={selectedService.name}
          botId={bot.id}
        />
      )}
    </div>
  );
};

export default BotCard;
